// Milestones — what the writing has given back.
//
// Unearned badges are shown veiled with a progress meter rather than hidden. The pull is knowing
// what exists and how far off it is; a case that only shows what you already have has nothing to
// say to the person who most needs a reason to keep going. Governing rule from the reward catalog:
// pull, never guilt — there is no penalty state here, only distances.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMilestones, type Milestone, type MilestonesView } from '../api/rewards';
import { IconChevronLeft, IconOreia } from '../components/Icons';
import { EmptyState, Spinner } from '../components/ui';

export function MilestonesScreen() {
  const [view, setView] = useState<MilestonesView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const next = await fetchMilestones(controller.signal);
        if (!controller.signal.aborted) setView(next);
      } catch {
        if (!controller.signal.aborted) setView(null);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  if (loading) {
    return (
      <div style={{ paddingTop: 60 }}>
        <Spinner label="Gathering your milestones…" />
      </div>
    );
  }

  const milestones = view?.milestones ?? [];

  return (
    <>
      <div className="ohead">
        <Link className="obrand ocard-link" to="/self" aria-label="Back to Self">
          <IconChevronLeft />
          <span className="nm" style={{ fontSize: 22 }}>Milestones</span>
        </Link>
      </div>

      <div className="ahero self-hero">
        <span className="olabel akick">Milestones</span>
        <h1>
          What the writing
          <br />
          <em>has given back.</em>
        </h1>
      </div>

      {milestones.length === 0 ? (
        <EmptyState
          title="No milestones yet."
          body="They arrive as you write. Nothing is being withheld — there is simply nothing to show until the first entry."
          action={
            <Link className="octa" to="/write" style={{ textDecoration: 'none' }}>
              Write an entry
            </Link>
          }
        />
      ) : (
        <>
          <p className="obody" style={{ margin: '0 0 20px' }}>
            {view!.earnedCount} of {milestones.length} gathered
            {view!.next
              ? ` · ${view!.next.remaining} more ${view!.next.remaining === 1 ? 'entry' : 'entries'} until ${view!.next.title}`
              : ' · every one of them'}
          </p>
          <div className="bdg-grid">
            {milestones.map((m) => (
              <MilestoneCard key={m.key} milestone={m} entryCount={view!.entryCount} />
            ))}
          </div>
        </>
      )}

      <div style={{ height: 60 }} />
    </>
  );
}

function MilestoneCard({ milestone, entryCount }: { milestone: Milestone; entryCount: number }) {
  const earned = milestone.earnedAt !== null;
  const done = Math.min(entryCount, milestone.milestone);
  const percent = milestone.milestone > 0 ? Math.round((100 * done) / milestone.milestone) : 0;

  return (
    <div className="bdg-card">
      <span className={`bdg-orb lg${earned ? '' : ' veiled'}`} aria-hidden>
        <IconOreia />
      </span>
      <span className="bn">{milestone.title}</span>
      <span className="bh">{milestone.description}</span>
      {earned ? (
        <span className="bmeta">EARNED{formatEarned(milestone.earnedAt)}</span>
      ) : (
        <>
          <div
            className="ometer"
            role="progressbar"
            aria-valuenow={done}
            aria-valuemin={0}
            aria-valuemax={milestone.milestone}
            aria-label={`${milestone.title}: ${done} of ${milestone.milestone} entries`}
          >
            <i style={{ width: `${percent}%` }} />
          </div>
          <span className="bmeta">
            {done} / {milestone.milestone}
          </span>
        </>
      )}
    </div>
  );
}

function formatEarned(iso: string | null): string {
  if (!iso) return '';
  const parsed = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  return ` ${parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }).toUpperCase()}`;
}
